import { useRef, useState, useEffect } from "react";
import { useInView, motion } from "framer-motion";
import { Mail, Instagram, Linkedin, ChevronDown, Calendar } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import emailjs from "@emailjs/browser";
import {
  containerVariants,
  titleVariants,
  textVariants,
} from "../utils/animations";
import visitCard from "../assets/visit_card.webp";

// Individual Contact Info Item Component
const ContactInfoItem = ({ info }) => {
  const itemRef = useRef(null);
  const isItemInView = useInView(itemRef, { once: true, margin: "-50px" });

  const itemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const iconVariants = {
    hidden: { opacity: 0, scale: 0, rotate: -180 },
    visible: {
      opacity: 1,
      scale: 1,
      rotate: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2,
      },
    },
  };

  const IconComponent = info.icon;

  const content = (
    <>
      <motion.div
        className="w-12 h-12 bg-[#C5A66A] rounded-lg flex items-center justify-center text-[#0F1B2D]"
        variants={iconVariants}
        initial="hidden"
        animate={isItemInView ? "visible" : "hidden"}
        aria-hidden="true"
      >
        <IconComponent size={24} />
      </motion.div>
      <div>
        <motion.p
          className="font-semibold"
          initial={{ opacity: 0, y: 10 }}
          animate={isItemInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{ duration: 0.4, delay: 0.3 }}
        >
          {info.title}
        </motion.p>
        <motion.p
          className="text-gray-300"
          initial={{ opacity: 0, y: 10 }}
          animate={isItemInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{ duration: 0.4, delay: 0.4 }}
        >
          {info.value}
        </motion.p>
      </div>
    </>
  );

  return (
    <motion.div
      ref={itemRef}
      className="flex items-center space-x-4"
      variants={itemVariants}
      initial="hidden"
      animate={isItemInView ? "visible" : "hidden"}
    >
      {info.link ? (
        <a
          href={info.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center space-x-4 hover:opacity-80 transition-opacity"
          aria-label={`Visit ${info.title} profile`}
        >
          {content}
        </a>
      ) : (
        content
      )}
    </motion.div>
  );
};

// Individual Form Field Component
const FormField = ({ children, delay = 0 }) => {
  const fieldRef = useRef(null);
  const isFieldInView = useInView(fieldRef, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={fieldRef}
      initial={{ opacity: 0, y: 20 }}
      animate={isFieldInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.5, delay }}
    >
      {children}
    </motion.div>
  );
};

// Individual Contact Card Component
const ContactCard = ({ children, delay = 0, className = "" }) => {
  const cardRef = useRef(null);
  const isCardInView = useInView(cardRef, { once: true, margin: "-50px" });

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: "easeOut",
        delay,
      },
    },
  };

  return (
    <motion.div
      ref={cardRef}
      className={`bg-[#1a2b42] p-8 rounded-2xl ${className}`}
      variants={cardVariants}
      initial="hidden"
      animate={isCardInView ? "visible" : "hidden"}
    >
      {children}
    </motion.div>
  );
};

// Custom Select Component
const CustomSelect = ({
  name,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  delay = 0,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef(null);

  const handleSelect = (optionValue) => {
    onChange({ target: { name, value: optionValue } });
    setIsOpen(false);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setIsOpen(!isOpen);
    } else if (event.key === "Escape" && isOpen) {
      setIsOpen(false);
    }
  };

  const selectedOption = options.find((option) => option.value === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (selectRef.current && !selectRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <FormField delay={delay}>
      <div className="relative" ref={selectRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          className={`w-full p-4 rounded-lg bg-[#0F1B2D] text-white border border-gray-600 focus:border-[#C5A66A] focus:outline-none flex items-center justify-between ${
            !selectedOption ? "text-gray-400" : "text-white"
          }`}
          required={required}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-label={`Select ${placeholder.toLowerCase()}`}
        >
          <span>{selectedOption ? selectedOption.label : placeholder}</span>
          <ChevronDown
            size={20}
            className={`transition-transform duration-200 ${
              isOpen ? "rotate-180" : ""
            }`}
            aria-hidden="true"
          />
        </button>

        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute z-10 w-full mt-1 bg-[#1a2b42] border border-gray-600 rounded-lg shadow-lg max-h-60 overflow-auto"
            role="listbox"
            aria-label={`${placeholder} options`}
          >
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`w-full px-4 py-3 text-left hover:bg-[#0F1B2D] transition-colors duration-200 ${
                  value === option.value
                    ? "bg-[#C5A66A] text-[#0F1B2D]"
                    : "text-white"
                }`}
                role="option"
                aria-selected={value === option.value}
              >
                {option.label}
              </button>
            ))}
          </motion.div>
        )}
      </div>
    </FormField>
  );
};

// Contact Section
const ContactSection = () => {
  const { t, i18n } = useTranslation();
  // Rechtstexte: deutsche Fassung für DE, sonst die englische (siehe Footer.jsx).
  const isDE = i18n.language?.startsWith("de");
  const privacyHref = isDE ? "/privacy" : "/en/privacy";
  const imprintHref = isDE ? "/impressum" : "/en/imprint";
  const navigate = useNavigate();
  const year = "2026"; // konstant wie in Footer.jsx — Prerender und Hydration bleiben identisch
  const headerRef = useRef(null);
  const isHeaderInView = useInView(headerRef, { once: true, margin: "-100px" });

  const footerRef = useRef(null);
  const isFooterInView = useInView(footerRef, { once: true, margin: "-50px" });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
    budget: "",
    service: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const serviceOptions = [
    { value: "strategie", label: t("contact.services.strategie") },
    { value: "ai", label: t("contact.services.ai") },
    { value: "digital", label: t("contact.services.digital") },
    { value: "consulting", label: t("contact.services.consulting") },
    { value: "other", label: t("contact.services.other") },
  ];

  const budgetOptions = [
    { value: "under-25k", label: t("contact.budgets.under-25k") },
    { value: "25k-100k", label: t("contact.budgets.25k-100k") },
    { value: "100k-250k", label: t("contact.budgets.100k-250k") },
    { value: "250k-1M", label: t("contact.budgets.250k-1M") },
    { value: "over-1M", label: t("contact.budgets.over-1M") },
    { value: "discuss", label: t("contact.budgets.discuss") },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const result = await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        {
          from_name: formData.name,
          from_email: formData.email,
          message: formData.message,
          budget: formData.budget,
          service: formData.service,
        },
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY
      );

      if (result.status === 200) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          message: "",
          budget: "",
          service: "",
        });
      }
    } catch (error) {
      console.error("EmailJS Error:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "E-Mail",
      value: "contact@khans.ai",
      link: "mailto:contact@khans.ai",
    },
    {
      icon: Calendar,
      title: "Strategy Call (15 Min)",
      value: "Termin direkt buchen",
      link: "https://cal.eu/d.-khan-ggqskh/15min",
    },
    {
      icon: Calendar,
      title: "Deep Dive (30 Min)",
      value: "Termin direkt buchen",
      link: "https://cal.eu/d.-khan-ggqskh/30min",
    },
    {
      icon: Instagram,
      title: "Instagram",
      value: "@dee2kay",
      link: "https://www.instagram.com/dee2kay/",
    },
    {
      icon: Linkedin,
      title: "LinkedIn",
      value: "@daaniyalkhan",
      link: "https://www.linkedin.com/in/daaniyal-khan-667813149/",
    },
  ];

  const footerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      className="pt-32 pb-10 bg-[#0F1B2D] text-white"
      aria-labelledby="contact-title"
    >
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            ref={headerRef}
            variants={containerVariants}
            initial="hidden"
            animate={isHeaderInView ? "visible" : "hidden"}
          >
            <motion.h2
              id="contact-title"
              className="font-playfair text-[#C5A66A] text-4xl lg:text-5xl font-bold mb-6"
              variants={titleVariants}
            >
              {t("contact.title")}
            </motion.h2>
            <motion.p
              className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto"
              variants={textVariants}
            >
              {t("contact.subtitle")}
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <ContactCard delay={0.4} className="order-1 lg:order-2">
              <motion.h3
                className="text-2xl font-bold mb-6 text-[#C5A66A]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
              >
                {t("contact.quickContact")}
              </motion.h3>
              <form
                onSubmit={handleSubmit}
                className="space-y-4"
                aria-label="Contact form"
              >
                <FormField delay={0.6}>
                  <label htmlFor="name" className="sr-only">
                    {t("contact.form.name")}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder={t("contact.form.name")}
                    required
                    className="w-full p-4 rounded-lg bg-[#0F1B2D] text-white placeholder-gray-400 border border-gray-600 focus:border-[#C5A66A] focus:outline-none"
                    aria-label={t("contact.form.name")}
                  />
                </FormField>
                <FormField delay={0.7}>
                  <label htmlFor="email" className="sr-only">
                    {t("contact.form.email")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={t("contact.form.email")}
                    required
                    className="w-full p-4 rounded-lg bg-[#0F1B2D] text-white placeholder-gray-400 border border-gray-600 focus:border-[#C5A66A] focus:outline-none"
                    aria-label={t("contact.form.email")}
                  />
                </FormField>
                <CustomSelect
                  name="service"
                  value={formData.service}
                  onChange={handleInputChange}
                  options={serviceOptions}
                  placeholder={t("contact.form.service")}
                  required
                  delay={0.75}
                />
                <CustomSelect
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  options={budgetOptions}
                  placeholder={t("contact.form.budget")}
                  required
                  delay={0.8}
                />
                <FormField delay={0.85}>
                  <label htmlFor="message" className="sr-only">
                    {t("contact.form.message")}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={t("contact.form.message")}
                    rows="4"
                    required
                    className="w-full p-4 rounded-lg bg-[#0F1B2D] text-white placeholder-gray-400 border border-gray-600 focus:border-[#C5A66A] focus:outline-none resize-none"
                    aria-label={t("contact.form.message")}
                  />
                </FormField>

                {/* Status Messages */}
                {submitStatus === "success" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 bg-green-600 text-white rounded-lg text-sm"
                    role="alert"
                    aria-live="polite"
                  >
                    {t("contact.form.success")}
                  </motion.div>
                )}

                {submitStatus === "error" && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 bg-red-600 text-white rounded-lg text-sm"
                    role="alert"
                    aria-live="polite"
                  >
                    {t("contact.form.error")}
                  </motion.div>
                )}

                <FormField delay={0.9}>
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full p-4 rounded-lg font-semibold transition-colors duration-300 ${
                      isSubmitting
                        ? "bg-gray-500 cursor-not-allowed"
                        : "bg-[#C5A66A] text-[#0F1B2D] hover:bg-[#b8966b]"
                    }`}
                    whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                    whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    aria-label={
                      isSubmitting
                        ? t("contact.form.sending")
                        : t("contact.form.send")
                    }
                  >
                    {isSubmitting
                      ? t("contact.form.sending")
                      : t("contact.form.send")}
                  </motion.button>
                </FormField>
              </form>
            </ContactCard>

            <ContactCard delay={0.2} className="order-2 lg:order-1">
              <motion.h3
                className="text-2xl font-bold mb-6 text-[#C5A66A]"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
              >
                {t("contact.directContact")}
              </motion.h3>
              <div className="flex flex-col h-full">
                <div className="space-y-4 text-left">
                  {contactInfo.map((info, index) => (
                    <ContactInfoItem key={index} info={info} />
                  ))}
                </div>

                {/* Visit Card Image */}
                <motion.div
                  className="mb-6 flex-1 flex items-center justify-center "
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.8 }}
                >
                  <img
                    src={visitCard}
                    alt="Daaniyal Khan's business card"
                    className="w-full h-auto object-cover rounded-lg shadow-xl"
                    style={{
                      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
                    }}
                  />
                </motion.div>
              </div>
            </ContactCard>
          </div>

          <motion.div
            ref={footerRef}
            className="mt-12 pt-8 border-t border-gray-700"
            variants={footerVariants}
            initial="hidden"
            animate={isFooterInView ? "visible" : "hidden"}
          >
            <p className="text-gray-400">
              © {year} Daaniyal Khan. {t("footer.rights")} |
              <a
                className="text-[#C5A66A] hover:underline cursor-pointer"
                href={imprintHref}
                aria-label="Go to imprint page"
              >
                {" "}
                {t("footer.impressum")}
              </a>{" "}
              |
              <a
                className="text-[#C5A66A] hover:underline cursor-pointer"
                href={privacyHref}
                aria-label="Go to privacy policy page"
              >
                {" "}
                {t("footer.datenschutz")}
              </a>
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
